import { Component, EventEmitter, Input, Output } from '@angular/core';
import { getTranslationProvider } from '@core/i18n/getTranslationProvider';
import { TranslatableComponent } from '@core/i18n/TranslatableComponent';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Client } from 'src/app/core/entity/client';
import { LundifyConnection } from 'src/app/core/entity/lundify-connection';
import {
  LundifyShareRecipient,
  LundifyShareRecipientRole,
  LundifySharedDocument,
} from 'src/app/core/entity/lundify-shared-document';
import { CloudCompanyService } from 'src/app/core/services/cloud-company.service';
import { DocumentService } from 'src/app/core/services/document.service';
import { ToastActions } from 'src/app/core/state/toast/toast.actions';

// The "Share <filename>" / "Manage access" overlay. Two entry points:
// - a freshly picked file (`file`): uploaded once with the drafted recipients, or without any
//   recipients ("Upload without sharing").
// - an already uploaded document (`sharedDocument`): its current recipients are preloaded.
// Everything the user does is a local draft. Nothing is sent to the backend until "Share" is
// clicked; closing the overlay discards the draft. For an existing document the draft replaces the
// whole recipient list in one call (PUT /sp/document/shared/{id}/recipients), so the file is never re-sent.
@Component({
  selector: 'app-lundify-share-dialog',
  templateUrl: './lundify-share-dialog.component.html',
  providers: [getTranslationProvider('LundifyShareDialogComponent')],
})
export class LundifyShareDialogComponent extends TranslatableComponent {
  @Input() client: Client | undefined;

  @Input()
  get visible(): boolean {
    return this._visible;
  }
  set visible(value: boolean) {
    const wasVisible = this._visible;
    this._visible = value;

    if (value && !wasVisible) {
      this.resetState();
      this.loadLundifyUsers();
    }
  }

  // The file picked by the user, not yet uploaded.
  @Input() file: File | null = null;

  // An already uploaded document whose access is managed; mutually exclusive with `file`.
  @Input() sharedDocument: LundifySharedDocument | null = null;

  // Destination folder (slash-separated, no file name) sent as the share's "path"; empty = root.
  @Input() path = '';

  @Output() visibleChange = new EventEmitter<boolean>();

  // Emitted when something changed on the BLA side, so the caller can refresh the list.
  @Output() shared = new EventEmitter<void>();

  roleOptions: { label: string; value: LundifyShareRecipientRole }[] = [];

  lundifyUsers: LundifyConnection[] = [];
  loadingUsers = false;
  sending = false;

  selectedUserId: number | null = null;
  access: LundifyShareRecipient[] = [];

  // Recipients as they are in BLA when the overlay opens (empty for a new file).
  private original: LundifyShareRecipient[] = [];
  private submitted = false;
  private _visible = false;

  constructor(
    private cloudCompanyService: CloudCompanyService,
    private documentService: DocumentService,
  ) {
    super();
  }

  protected override onTranslationsLoaded(): void {
    this.roleOptions = [
      { label: this.translate('roleRead').toString(), value: 'READ' },
      { label: this.translate('roleApprove').toString(), value: 'APPROVE' },
    ];
  }

  get fileName(): string {
    return this.file?.name ?? this.sharedDocument?.fileName ?? '';
  }

  get canAdd(): boolean {
    return !!this.selectedUserId && !this.sending;
  }

  get canAddAll(): boolean {
    return this.invitableUsers.length > 0 && !this.sending && !this.loadingUsers;
  }

  get canShare(): boolean {
    if (this.sending) {
      return false;
    }
    return this.file ? this.access.length > 0 : this.hasChanges;
  }

  // The draft holds recipients, so uploading without sharing would silently throw them away.
  get canUploadWithoutRecipients(): boolean {
    return !!this.file && this.access.length === 0 && !this.sending;
  }

  // Users already in the draft are dropped from the picker so the same person can't be added twice.
  get invitableUsers(): LundifyConnection[] {
    return this.lundifyUsers.filter((user) => !this.access.some((granted) => granted.cloudUserId === user.id));
  }

  private get hasChanges(): boolean {
    if (this.access.length !== this.original.length) {
      return true;
    }
    return this.access.some((granted) => {
      const before = this.original.find((candidate) => candidate.cloudUserId === granted.cloudUserId);
      return !before || before.role !== granted.role;
    });
  }

  userLabel(user: LundifyConnection): string {
    return user.email ? `${user.name} (${user.email})` : user.name;
  }

  userName(cloudUserId: number): string {
    const user = this.lundifyUsers.find((candidate) => candidate.id === cloudUserId);
    return user?.name ?? user?.email ?? `#${cloudUserId}`;
  }

  add() {
    if (!this.canAdd || !this.selectedUserId) {
      return;
    }

    this.access = [...this.access, { cloudUserId: this.selectedUserId, role: 'READ' }];
    this.selectedUserId = null;
  }

  addAll() {
    if (!this.canAddAll) {
      return;
    }

    this.access = [
      ...this.access,
      ...this.invitableUsers.map((user) => ({ cloudUserId: user.id, role: 'READ' as LundifyShareRecipientRole })),
    ];
    this.selectedUserId = null;
  }

  changeRole(granted: LundifyShareRecipient, role: LundifyShareRecipientRole) {
    granted.role = role;
  }

  remove(granted: LundifyShareRecipient) {
    this.access = this.access.filter((candidate) => candidate !== granted);
  }

  uploadWithoutRecipients() {
    if (!this.canUploadWithoutRecipients || !this.client || !this.file) {
      return;
    }

    this.send(this.documentService.shareDocumentToLundify(this.client.id, this.file, [], this.path));
  }

  share() {
    if (!this.canShare || !this.client) {
      return;
    }

    const recipients = this.access.map(({ cloudUserId, role }) => ({ cloudUserId, role }));
    if (this.file) {
      this.send(this.documentService.shareDocumentToLundify(this.client.id, this.file, recipients, this.path));
      return;
    }

    if (this.sharedDocument) {
      this.send(
        this.documentService.replaceSharedDocumentRecipients(this.client.id, this.sharedDocument.documentShareId, recipients),
      );
    }
  }

  // Runs after the p-dialog hide animation. The parent is only notified here, so it can't open the
  // next queued file while this dialog is still hiding.
  onHidden() {
    if (this.sending) {
      this._visible = true;
      return;
    }

    if (this.file && !this.submitted) {
      this.store.dispatch(
        ToastActions.showInfoMessage({
          summary: this.translate('notSharedToastTitle').toString(),
          detail: this.translate('notSharedToastDetail').toString(),
        }),
      );
    }
    this._visible = false;
    this.visibleChange.emit(false);
  }

  private send(request: Observable<unknown>) {
    this.sending = true;
    request.pipe(finalize(() => (this.sending = false))).subscribe({
      next: () => {
        this.submitted = true;
        this.shared.emit();
        this._visible = false;
      },
      error: () => {},
    });
  }

  private loadLundifyUsers() {
    if (!this.client) {
      return;
    }

    this.loadingUsers = true;
    this.cloudCompanyService.getLundifyCompanySubscriptionUsers(this.client.id).subscribe({
      next: (users) => {
        this.lundifyUsers = (users || []).filter((user) => !!user.id);
        this.loadingUsers = false;
      },
      error: () => {
        this.lundifyUsers = [];
        this.loadingUsers = false;
      },
    });
  }

  private resetState() {
    this.original = (this.sharedDocument?.recipients ?? []).map(({ cloudUserId, role }) => ({ cloudUserId, role }));
    this.access = this.original.map((granted) => ({ ...granted }));
    this.selectedUserId = null;
    this.sending = false;
    this.submitted = false;
  }
}
